import type { Configuration, PopupRequest, NavigationOptions } from '@azure/msal-browser';
import { NavigationClient } from '@azure/msal-browser';
import type { NavigateFunction } from 'react-router-dom';

/**
 * Azure AD / MSAL Configuration
 *
 * NOTE: This is a placeholder configuration.
 * Actual Azure AD integration will be implemented in Story 1.6.
 *
 * Environment variables required:
 * - VITE_AZURE_CLIENT_ID: Azure AD application (client) ID
 * - VITE_AZURE_TENANT_ID: Azure AD directory (tenant) ID
 * - VITE_AZURE_REDIRECT_URI: Redirect URI configured in Azure AD
 */

/**
 * Custom navigation client for react-router integration.
 * Uses react-router navigate to avoid full page reloads after MSAL redirect,
 * which would clear React state before MSAL finishes writing tokens to sessionStorage.
 */
export class CustomNavigationClient extends NavigationClient {
	private navigate: NavigateFunction;

	constructor(navigate: NavigateFunction) {
		super();
		this.navigate = navigate;
	}

	async navigateInternal(url: string, options: NavigationOptions) {
		const relativePath = url.replace(window.location.origin, '');
		this.navigate(relativePath, { replace: !!options.noHistory });
		return false;
	}
}

/**
 * MSAL Configuration object
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
	auth: {
		clientId: import.meta.env.VITE_AZURE_CLIENT_ID, // Placeholder
		authority: import.meta.env.VITE_AZURE_TENANT_ID
			? `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID}`
			: 'https://login.microsoftonline.com/common', // Placeholder
		redirectUri: '/',
		postLogoutRedirectUri: '/',
	},
	cache: {
		cacheLocation: 'localStorage',
	},
	system: {
		loggerOptions: {
			loggerCallback: (level, message, containsPii) => {
				if (containsPii) {
					return;
				}
				switch (level) {
					case 0: // LogLevel.Error
						console.error(message);
						return;
					case 1: // LogLevel.Warning
						console.warn(message);
						return;
					case 2: // LogLevel.Info
						console.info(message);
						return;
					case 3: // LogLevel.Verbose
						console.debug(message);
						return;
					default:
						return;
				}
			},
		},
	},
};

/**
 * Scopes for login request
 * Add API scopes here when backend API is configured
 */
export const loginRequest: PopupRequest = {
	scopes: [
		'User.Read', // Microsoft Graph API - User profile
		import.meta.env.VITE_AZURE_API_SCOPE,
	],
};

/**
 * Scopes for API token acquisition
 */
export const apiRequest = {
	scopes: [import.meta.env.VITE_AZURE_API_SCOPE],
};
